import type { ReactNode } from "react";
import type { ComponentType } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface KpiCardProps {
  label: string;
  value: ReactNode;
  icon: ComponentType<{ className?: string }>;
  loading?: boolean;
  onClick?: () => void;
  tone?: "default" | "success" | "warning" | "danger";
  className?: string;
  // Override the value text styling for a single card. When omitted, the
  // default prominent, non-wrapping, semibold treatment is used — so passing
  // this never changes the shared/global appearance of other KPI cards.
  valueClassName?: string;
}

const toneStyles = {
  default: "bg-primary/10 text-primary",
  success: "bg-success/10 text-success",
  warning: "bg-warning/10 text-warning",
  danger: "bg-destructive/10 text-destructive",
};

export function KpiCard({ label, value, icon: Icon, loading, onClick, tone = "default", className, valueClassName }: KpiCardProps) {
  const content = (
    <CardContent className="flex h-full min-h-28 items-start justify-between gap-3 p-5">
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm font-medium text-muted-foreground">{label}</p>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          // Default: values must never wrap (especially currency decimals) —
          // shrink the type instead, and keep digits tabular for column
          // alignment. valueClassName overrides this for callers that need a
          // wrapping, lighter status treatment on a single card.
          <p className={valueClassName ?? "font-heading text-[clamp(1.05rem,1.1vw+0.75rem,1.5rem)] font-semibold leading-tight text-foreground whitespace-nowrap tabular-nums"}>
            {value}
          </p>
        )}
      </div>

      <span className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", toneStyles[tone])}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
    </CardContent>
  );

  return (
    <Card className={cn("h-full", onClick && "transition hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-card-hover", className)}>
      {onClick ? (
        <button type="button" className="h-full w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset" onClick={onClick}>
          {content}
        </button>
      ) : content}
    </Card>
  );
}